'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { scrubIndexFromClientX, type ScrubGeometry } from '@/lib/chart/scrub';

/**
 * Crosshair/readout gesture handling shared by the SVG scrub charts
 * (MiniChart, PopupChart). Mouse hover works as it always has; the rest of
 * this exists because touch was effectively unusable:
 *
 *   - a touch pointer only emits `pointermove` while the finger is down AND
 *     moving, so with a move-only handler a tap did nothing and you had to
 *     press-and-drag to get a first reading;
 *   - `pointerleave` fires immediately after `pointerup` for touch (the
 *     pointer ceases to exist), so honouring it wiped the readout the instant
 *     the finger lifted — you had to keep holding the screen to read a price.
 *
 * So: `pointerdown` seeds the readout (a plain tap works), the pointer is
 * captured for the drag, `pointerleave` is ignored for non-mouse pointers, and
 * a lifted finger leaves the readout up for `holdMs` before it clears.
 */

/** How long the readout stays up after a finger lifts — long enough to read a
 *  price without leaving a stale crosshair parked on the chart. */
export const TOUCH_HOLD_MS = 4000;

export interface UseChartScrubOptions extends ScrubGeometry {
  /** Off = no listeners do anything and `touch-action` is left alone (dashboard
   *  tiles render MiniChart too small for a useful readout). Default true. */
  enabled?: boolean;
  /** Override the post-lift hold. 0 clears as soon as the finger leaves. */
  holdMs?: number;
}

export interface ChartScrubHandlers {
  onPointerDown: (e: React.PointerEvent<SVGSVGElement>) => void;
  onPointerMove: (e: React.PointerEvent<SVGSVGElement>) => void;
  onPointerUp: (e: React.PointerEvent<SVGSVGElement>) => void;
  onPointerCancel: (e: React.PointerEvent<SVGSVGElement>) => void;
  onPointerLeave: (e: React.PointerEvent<SVGSVGElement>) => void;
}

export interface UseChartScrubResult {
  /** Attach to the chart's `<svg>`. */
  svgRef: React.MutableRefObject<SVGSVGElement | null>;
  /** Bar index under the pointer, or null when nothing is being read. */
  index: number | null;
  handlers: ChartScrubHandlers;
  /** Spread onto the `<svg>` alongside `handlers`. */
  style: React.CSSProperties | undefined;
  clear: () => void;
}

export function useChartScrub({
  viewW, padLeft, plotW, count,
  enabled = true,
  holdMs = TOUCH_HOLD_MS,
}: UseChartScrubOptions): UseChartScrubResult {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [index, setIndex] = useState<number | null>(null);
  /** pointerId of an in-progress press-drag; null when merely hovering. */
  const activeRef = useRef<number | null>(null);
  const holdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelHold = useCallback(() => {
    if (holdRef.current) { clearTimeout(holdRef.current); holdRef.current = null; }
  }, []);

  const clear = useCallback(() => { cancelHold(); setIndex(null); }, [cancelHold]);

  // Don't let a pending hold fire into an unmounted popup.
  useEffect(() => cancelHold, [cancelHold]);
  useEffect(() => { if (!enabled) clear(); }, [enabled, clear]);

  const track = useCallback((clientX: number, clamp: boolean) => {
    const svg = svgRef.current;
    if (!svg) return;
    setIndex(scrubIndexFromClientX(clientX, svg.getBoundingClientRect(), { viewW, padLeft, plotW, count }, clamp));
  }, [viewW, padLeft, plotW, count]);

  const onPointerDown = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    if (!enabled) return;
    cancelHold();
    activeRef.current = e.pointerId;
    // Capture so a finger that slides off the chart (or past its edge) keeps
    // scrubbing instead of silently dropping the gesture.
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* unsupported — degrade to uncaptured */ }
    track(e.clientX, true);
  }, [enabled, cancelHold, track]);

  const onPointerMove = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    if (!enabled) return;
    // Clamp only while dragging. A hovering mouse can legitimately move into
    // the axis gutter, where the crosshair should clear rather than stick.
    track(e.clientX, activeRef.current === e.pointerId);
  }, [enabled, track]);

  const endScrub = useCallback((e: React.PointerEvent<SVGSVGElement>, keep: boolean) => {
    if (activeRef.current !== e.pointerId) return;
    activeRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* already released */ }
    if (!keep) { clear(); return; }
    // A mouse keeps hovering after the button comes up, so leave it alone and
    // let pointerleave do the clearing. A finger is gone — hold the readout.
    if (e.pointerType === 'mouse') return;
    cancelHold();
    if (holdMs <= 0) { setIndex(null); return; }
    holdRef.current = setTimeout(() => { holdRef.current = null; setIndex(null); }, holdMs);
  }, [cancelHold, clear, holdMs]);

  const onPointerUp = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    if (!enabled) return;
    endScrub(e, true);
  }, [enabled, endScrub]);

  const onPointerCancel = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    if (!enabled) return;
    // The browser took the gesture over (e.g. a vertical swipe became a page
    // scroll) — drop the readout rather than leaving it stranded mid-drag.
    endScrub(e, false);
  }, [enabled, endScrub]);

  const onPointerLeave = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    if (!enabled) return;
    // Only a mouse actually "leaves". For touch this fires right after
    // pointerup, and acting on it is what used to kill the readout on lift.
    if (e.pointerType !== 'mouse') return;
    if (activeRef.current !== null) return;
    clear();
  }, [enabled, clear]);

  // A timeframe switch can shrink the bar set under a held index.
  const safeIndex = index != null && count > 0 ? Math.min(index, count - 1) : null;

  return {
    svgRef,
    index: safeIndex,
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onPointerLeave },
    // pan-y keeps vertical page scrolling working over the chart while giving
    // horizontal drags to the scrubber — without it the browser claims the
    // gesture and cancels the scrub.
    style: enabled ? { touchAction: 'pan-y' } : undefined,
    clear,
  };
}
